import {
  Box,
  Button,
  capitalize,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import {
  DataLoader,
  DataRequest,
  DataRequestState,
  useDataRequest,
} from "../data";
import { useT } from "../../i18n";
import { Umsatz } from "../../api";
import DataTable from "../data/DataTable";
import useCustomFilters from "../data/useCustomFilters";
import { TextField } from "@mui/material";
import { formatAsDecimal } from "../../formatterHelpers";
import { Field, Formik } from "formik";
import BaustellenFilterSelect from "./BaustellenFilterSelect";
import SubmitButton from "../SubmitButton";
import useEntsorgungsdokumentation from "../../hooks/useEntsorgungsdokumentation";
import ShowFormikErrors from "../allgemein/ShowFormikErrors";
import { useSucheUmsatz } from "../../client/hooks";
import SearchField from "../SearchField";
import { TFunction } from "i18next";
import { useEntsorgungsdokumentationSelection } from "../../contexts/EntsorgungsdokumentationSelectionContext";

export type EntsorgungsdokumentationFilter = {
  search?: string;
  filtered?: string;
  baustelleId?: string;
  startDate?: string;
  endDate?: string;
  preiseAuswahl?: boolean;
  bemerkungenAuswahl?: boolean;
};

export type Props = Omit<
  DataRequest<EntsorgungsdokumentationFilter>,
  "filter"
> &
  Partial<Pick<DataRequest<EntsorgungsdokumentationFilter>, "filter">>;

const tableName: string = "entsorgungsdokumentation";

const normalizeUmsatzRows = (items: Umsatz[]): Umsatz[] => {
  const toNum = (v: any): number =>
    typeof v === "number" && !Number.isNaN(v) ? v : Number(v) || 0;
  return items.map((row) => ({
    ...row,
    id: row.id,
    summeGesamtMengeCBM:
      row.summeGesamtMengeCBM == null ? 0 : toNum(row.summeGesamtMengeCBM),
    summeGesamtMengeTO:
      row.summeGesamtMengeTO == null ? 0 : toNum(row.summeGesamtMengeTO),
    eakSchluessel: row.eakSchluessel ?? "",
    materialId: row.materialId ?? "",
    abladestelleBezeichnung: row.abladestelleBezeichnung ?? undefined,
    abstellortBaustelleBezeichnung:
      row.abstellortBaustelleBezeichnung ?? undefined,
  }));
};

export default function EntsorgungsdokumentationDataTable({ ...input }: Props) {
  const [filteredState, setFilteredState] = useState("");
  const request = useDataRequest<EntsorgungsdokumentationFilter>({
    filter: {},
    ...input,
  });

  const { currentCustomFilter, setCustomField } =
    useCustomFilters<EntsorgungsdokumentationFilter>({
      tableName: tableName,
      defaultFilter: {
        search: "",
        baustelleId: "",
        startDate: "",
        endDate: new Date().toISOString().split("T")[0],
        preiseAuswahl: false,
        bemerkungenAuswahl: false,
      },
    });

  const {
    onSubmitGeneriereEntsorgungsdoku,
    isLoadingPreview,
    isGewaehlt,
    handlePreviewClick,
    translation,
  } = useEntsorgungsdokumentation(currentCustomFilter);

  return (
    <Stack spacing={2}>
      <FilterComp
        request={request}
        filteredStateFilter={filteredState}
        currentCustomFilter={currentCustomFilter}
        setCustomField={setCustomField}
        onSubmitGeneriereEntsorgungsdoku={onSubmitGeneriereEntsorgungsdoku}
        isLoadingPreview={isLoadingPreview}
        isGewaehlt={isGewaehlt}
        handlePreviewClick={handlePreviewClick}
        translation={translation}
      />
      <EntsorgungsdokumentationResults
        request={request}
        setFilteredState={setFilteredState}
      />
    </Stack>
  );
}

function FilterComp({
  request,
  filteredStateFilter,
  currentCustomFilter,
  setCustomField,
  onSubmitGeneriereEntsorgungsdoku,
  isLoadingPreview,
  isGewaehlt,
  handlePreviewClick,
  translation,
}: {
  request: DataRequestState<EntsorgungsdokumentationFilter>;
  filteredStateFilter: string;
  currentCustomFilter: EntsorgungsdokumentationFilter;
  setCustomField: (
    field: keyof EntsorgungsdokumentationFilter,
    value: any
  ) => void;
  onSubmitGeneriereEntsorgungsdoku: any;
  isLoadingPreview: boolean;
  isGewaehlt: boolean;
  handlePreviewClick: () => Promise<void>;
  translation: TFunction<string, undefined>;
}) {
  const { setFilter } = request;

  useEffect(() => {
    setFilter({
      search: currentCustomFilter.search,
      baustelleId: currentCustomFilter.baustelleId,
      startDate: currentCustomFilter.startDate,
      endDate: currentCustomFilter.endDate,
      filtered: filteredStateFilter || undefined,
    });
  }, [
    currentCustomFilter.search,
    currentCustomFilter.baustelleId,
    currentCustomFilter.startDate,
    currentCustomFilter.endDate,
    filteredStateFilter,
    setFilter,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
      }}
    >
      <Stack direction="row">
        <Formik
          initialValues={{
            baustelle: currentCustomFilter.baustelleId,
            startDatum: currentCustomFilter.startDate || "",
            endDatum:
              currentCustomFilter.endDate ||
              new Date().toISOString().split("T")[0],
            preiseAuswahl: currentCustomFilter.preiseAuswahl || false,
            bemerkungenAuswahl: currentCustomFilter.bemerkungenAuswahl || false,
          }}
          enableReinitialize
          onSubmit={onSubmitGeneriereEntsorgungsdoku}
        >
          {({ isSubmitting, errors, handleSubmit }) => {
            const hasRequired = Boolean(
              currentCustomFilter.baustelleId &&
                currentCustomFilter.startDate &&
                currentCustomFilter.endDate
            );
            const canProceed = hasRequired && isGewaehlt;
            return (
              <>
                <form id="new-entsorgungsdokumentation" onSubmit={handleSubmit}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.5fr 0.5fr 0.5fr 1fr 3fr",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <SearchField
                      value={currentCustomFilter.search}
                      onChange={(val) => setCustomField("search", val)}
                    />
                    <Field
                      size={"small"}
                      component={BaustellenFilterSelect}
                      name="baustelle"
                      label={capitalize(translation("construction-site"))}
                      setBaustelleChanged={(val: string) => {
                        setCustomField("baustelleId", val);
                      }}
                      required
                    />
                    <TextField
                      sx={{ minWidth: "150px" }}
                      size="small"
                      label={capitalize(translation("start-date"))}
                      type="date"
                      value={currentCustomFilter.startDate}
                      onChange={(e) => {
                        setCustomField("startDate", e.target.value);
                      }}
                      required
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: {
                          max: currentCustomFilter.endDate,
                        },
                      }}
                    />
                    <TextField
                      sx={{ minWidth: "150px" }}
                      size="small"
                      label={capitalize(translation("end-date"))}
                      type="date"
                      value={currentCustomFilter.endDate}
                      onChange={(e) => {
                        setCustomField("endDate", e.target.value);
                      }}
                      required
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: {
                          min: currentCustomFilter.startDate,
                        },
                      }}
                    />
                    <Stack direction={"row"}>
                      <FormControlLabel
                        sx={{ ml: 1 }}
                        control={
                          <Switch
                            checked={!!currentCustomFilter.preiseAuswahl}
                            onChange={(e) => {
                              setCustomField("preiseAuswahl", e.target.checked);
                            }}
                          />
                        }
                        label={capitalize(translation("prices"))}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!currentCustomFilter.bemerkungenAuswahl}
                            onChange={(e) => {
                              setCustomField(
                                "bemerkungenAuswahl",
                                e.target.checked
                              );
                            }}
                          />
                        }
                        label={capitalize(translation("remark"))}
                      />
                    </Stack>
                    <Stack
                      direction={"row"}
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <>
                        <ShowFormikErrors errors={errors}>
                          <Button
                            variant="contained"
                            color="secondary"
                            disabled={!canProceed || isLoadingPreview}
                            onClick={handlePreviewClick}
                          >
                            {isLoadingPreview ? (
                              <CircularProgress size={24} />
                            ) : (
                              capitalize(translation("preview"))
                            )}
                          </Button>
                        </ShowFormikErrors>

                        <SubmitButton
                          form="new-entsorgungsdokumentation"
                          type="submit"
                          variant="contained"
                          color="secondary"
                          loading={isSubmitting}
                          disabled={!canProceed}
                        >
                          {capitalize(translation("create"))}
                        </SubmitButton>
                      </>
                    </Stack>
                  </Box>
                </form>
              </>
            );
          }}
        </Formik>
      </Stack>
    </Box>
  );
}

function EntsorgungsdokumentationResults({
  request,
  setFilteredState,
}: {
  request: DataRequestState<EntsorgungsdokumentationFilter>;
  setFilteredState: (value: string) => void;
}) {
  return <Results request={request} setFilteredState={setFilteredState} />;
}

function Results({
  request,
  setFilteredState,
}: {
  request: DataRequestState<EntsorgungsdokumentationFilter>;
  setFilteredState: (value: string) => void;
}) {
  const sucheUmsatz = useSucheUmsatz();
  const {
    gewaehlteAbladestellenMaterialien,
    setGewaehlteAbladestellenMaterialien,
  } = useEntsorgungsdokumentationSelection();

  const loadEntsorgungsdokumentation: DataLoader<
    EntsorgungsdokumentationFilter,
    Umsatz
  > = useCallback(
    async (params) => {
      if (params.baustelleId && params.startDate && params.endDate) {
        const result = await sucheUmsatz({
          baustellenId: params.baustelleId,
          startDate: params.startDate,
          endDate: params.endDate,
          isEntsorgungsdokumentation: true,
          ...params,
        });
        return {
          ...result,
          items: normalizeUmsatzRows(result.items ?? []),
        };
      }
      return { items: [], totalElements: 0, totalPages: 0, page: 0, limit: 10 };
    },
    [sucheUmsatz]
  );

  const handleRowSelectionChange = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      setGewaehlteAbladestellenMaterialien(
        Array.from(newRowSelectionModel).map((id) => String(id))
      );
    },
    [setGewaehlteAbladestellenMaterialien]
  );

  return (
    <DataTable
      columns={useColumns()}
      request={request}
      queryKey={[tableName]}
      loadData={loadEntsorgungsdokumentation}
      name={tableName}
      setFiltered={setFilteredState}
      checkboxSelection={true}
      disableRowSelectionOnClick={true}
      rowSelectionModel={gewaehlteAbladestellenMaterialien}
      onRowSelectionModelChange={handleRowSelectionChange}
    />
  );
}

function useColumns(): Array<GridColDef<Umsatz>> {
  const { t: translation } = useT("entsorgungsdokumentation");

  return useMemo(
    () => [
      {
        field: "buchungsTyp",
        headerName: translation("column-typ"),
        flex: 1,
      },
      {
        field: "bezeichnung",
        headerName: translation("column-bezeichnung"),
        flex: 2,
        valueGetter: (_value, row) => {
          return row.buchungsTyp === "MA" && row.quelleBaustelle
            ? row.quelleBaustelle
            : row.abladestelleBezeichnung ?? row.abstellortBaustelleBezeichnung;
        },
      },
      {
        field: "materialName",
        headerName: translation("column-material"),
        flex: 1,
      },
      {
        field: "eakSchluessel",
        headerName: translation("column-eak-nummer"),
        flex: 1,
      },
      {
        field: "summeGesamtMengeCBM",
        headerName: translation("column-gesamtmenge-cbm"),
        type: "number",
        flex: 1,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          return formatAsDecimal(params.value);
        },
      },
      {
        field: "summeGesamtMengeTO",
        headerName: translation("column-gesamtmenge-to"),
        type: "number",
        flex: 1,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          return formatAsDecimal(params.value);
        },
      },
    ],
    [translation]
  );
}
